import React, { useEffect, useMemo, useRef } from 'react'
import { Button, Divider, InputRef, Space, Table } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/lib/table'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { useGroupsList } from './hooks'
import { UserGroup } from './types'
import { path } from '../../../../libs'
import { useNavigate } from 'react-router-dom'
import { Guard } from '../../../../features/session/guard'
import { useUrlFilters } from '../../../../libs/useUrlFilters'

const FILTER_CONFIG = {
  id: { type: 'string' as const },
  name: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'name', pagination: true },
  sortOrder: {
    type: 'string' as const,
    default: 'descend',
    pagination: true,
  },
}

const SEARCH_KEYS = ['id', 'name'] as const

export const GroupList: React.FC = () => {
  const navigate = useNavigate()

  const { userGroups, loading, totalCountOfUserGroups, onChangeGroupList } =
    useGroupsList()

  const { filters, setFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'name'
  const orderDirection = filters.sortOrder === 'descend' ? '-' : ''

  const searchText = useMemo(() => {
    const result: Record<string, string> = {}
    for (const key of SEARCH_KEYS) {
      const value = filters[key]
      if (value) result[key] = value
    }
    return result
  }, [filters])

  const searchInput = useRef<InputRef>(null)

  const setSearchText: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  > = (updater) => {
    const next =
      typeof updater === 'function' ? updater({ ...searchText }) : updater
    const updates: Record<string, any> = { page: undefined }
    for (const key of SEARCH_KEYS) {
      updates[key] = next[key] || undefined
    }
    setFilters(updates as any)
  }

  useEffect(() => {
    onChangeGroupList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: {},
    })
  }, [pageSize, page, orderBy, orderDirection, searchText])

  const columns: ColumnsType<UserGroup> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<UserGroup>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      ...getColumnSearchProps<UserGroup>(
        'name',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: UserGroup) => (
        <Guard permission="edit_roles">
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/groups/edit/${item.id}`)}
          />
        </Guard>
      ),
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Guard permission="edit_roles">
        <Button
          type="primary"
          onClick={() => navigate(path.groups.create())}
        >
          Create group
        </Button>
      </Guard>
      <Divider style={{ margin: 0 }} />
      <Table
        dataSource={userGroups}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfUserGroups,
        }}
        onChange={(pagination, _tableFilters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current) {
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          }
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize =
              pagination.pageSize === 10
                ? undefined
                : String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          const field = sorter.field || sorter.columnKey
          // @ts-ignore
          const order = sorter.order as 'ascend' | 'descend' | undefined
          if (field) {
            updates.sortField = field === 'name' ? undefined : String(field)
            updates.sortOrder =
              order === 'ascend'
                ? 'ascend'
                : order === 'descend'
                ? undefined
                : undefined
          }
          setFilters(updates as any)
        }}
      />
    </Space>
  )
}
